import {
  absMoney,
  compareMoney,
  serializeMoney,
  subtractMoney,
  sumMoney,
  tryParseMoney,
} from '@erp/shared-utils';

/**
 * Money derivation shared by the short-pay and over-payment thread summary
 * cards.
 *
 * ## Why this exists
 *
 * A collection thread's `metadata.shortPayAmount` does NOT have one stable
 * meaning, and the cards used to assume it did:
 *
 *   - `ShortPayDetectionService.openThreadFromCandidate` (payment-application
 *     hook + Famous org sweep) writes `shortPayAmount = invoice.balance_due` —
 *     the whole outstanding balance.
 *   - `detectShortPaysFromRemittanceAdvice` writes
 *     `shortPayAmount = balance_due − netApplied` — the residual short AFTER
 *     the remittance's stated amount is credited. Nothing has posted yet, so
 *     `invoice.total_paid` is still 0.
 *   - `refreshCollectionThreadTitlesForInvoices` later overwrites it with
 *     `balance_due` again, flipping the meaning under a thread that was opened
 *     from a remittance.
 *
 * The old card subtracted the pending remittance from `shortPayAmount`
 * unconditionally. On the remittance path that double-counted the remittance
 * ($100 short − $900 promised, clamped at zero) and rendered the headline
 * "Short" as **$0.00** on an invoice that is demonstrably short — while the
 * thread title right above it said "$100.00 short pay on INV-…".
 *
 * So the cards no longer read a discrepancy amount out of metadata. They derive
 * everything from two unambiguous quantities:
 *
 *   1. `outstanding` — what the invoice still owes today, before anything
 *      unposted lands. Live `ar_invoices.balance_due` first; metadata only as a
 *      fallback, and never the ambiguous `shortPayAmount` on a
 *      remittance-derived thread.
 *   2. `pendingRemittance` — what the customer's remittance advice says it is
 *      paying this invoice that has NOT been applied yet.
 *
 * `shortfall = outstanding − pendingRemittance`, which is exactly the backend's
 * `balanceDue − netApplied`. The card and the thread title now agree.
 *
 * All arithmetic goes through the `@erp/shared-utils` money helpers (Decimal),
 * never floats, and every amount crosses this boundary as a decimal string.
 */

/** Amounts below this are treated as rounding noise, not a real balance. */
const EPSILON = '0.005';

/** The minimum a remittance line must expose for the net-stated calculation. */
export interface StatedLine {
  remittanceAdviceId: string;
  invoiceId: string | null;
  statedAmount: string | null;
  kind: 'invoice_payment' | 'deduction';
  matchStatus: string;
}

/**
 * The minimum a posted payment application must expose. `bankTransactionId`,
 * the cash receipt it spends, is what ties an application back to the
 * remittance linked to that receipt.
 */
export interface AppliedLine {
  invoiceId: string | null;
  bankTransactionId: string | null;
  appliedAmount: string | null;
}

/**
 * The minimum a remittance advice must expose. `bankTransactionId` is the cash
 * receipt the remittance is linked to; a null means no cash has been matched
 * to it yet, so nothing from that advice can have posted.
 */
export interface AdviceLink {
  id: string;
  bankTransactionId: string | null;
}

/** Live money fields off the linked `ar_invoices` row. */
export interface InvoiceMoney {
  netAmount: string | null;
  totalPaid: string | null;
  balanceDue: string | null;
}

export interface NetStatedResult<T extends StatedLine> {
  /** Payments less withheld deductions, as a decimal string. */
  net: string;
  /** Magnitude of the deduction lines folded into `net`. */
  deductionTotal: string;
  contributingLines: T[];
}

/**
 * Whether a remittance line counts towards this invoice's stated intent.
 *
 * Shared by `netStatedForInvoice` and `contributingAdviceIds` so the two can
 * never disagree about which lines are usable. They must stay in lockstep: if
 * an advice enters `contributingAdviceIds` on the strength of a line that
 * `netStatedForInvoice` rejected, `appliedForRemittance` subtracts that
 * advice's posted applications from a `net` that never included the line. The
 * pending remittance comes out understated, and because `outstanding` (live
 * `balance_due`) already reflects that posted cash, the short is counted twice.
 */
function contributesForInvoice(
  line: StatedLine,
  invoiceId: string | null,
): boolean {
  return (
    line.invoiceId === invoiceId &&
    line.matchStatus !== 'rejected' &&
    tryParseMoney(line.statedAmount) !== null
  );
}

/**
 * What the customer said they were sending against ONE invoice, net of anything
 * they withheld on it.
 *
 * Deduction lines store a POSITIVE `statedAmount` and carry the sign in `kind`,
 * so they must be subtracted: an amount withheld is not money the customer said
 * they were paying. Summing raw `statedAmount` inflates "Received" and shrinks
 * "Short" by the deduction, telling a collector a short-paid invoice needs less
 * action than it does.
 *
 * Scoped to `invoiceId` rather than using the remittance's statement total,
 * which would also cover unrelated invoices. Rejected lines are excluded —
 * a human already dispositioned them.
 *
 * Mirrors the backend's `netApplied` (statedPaidTotal − deductionTotal) in
 * `remittanceNetApplied.ts`; the two must agree or the UI and the thread-opening
 * logic will disagree about the same invoice.
 */
export function netStatedForInvoice<T extends StatedLine>(
  lines: T[],
  invoiceId: string | null,
): NetStatedResult<T> {
  const contributingLines = lines.filter((li) =>
    contributesForInvoice(li, invoiceId),
  );

  const paid = sumMoney(
    contributingLines
      .filter((li) => li.kind !== 'deduction')
      .map((li) => tryParseMoney(li.statedAmount) ?? 0),
  );
  // Deductions are stored positive, but tolerate a negative so a double
  // negative can't flip a withheld amount back into coverage.
  const deductions = sumMoney(
    contributingLines
      .filter((li) => li.kind === 'deduction')
      .map((li) => absMoney(tryParseMoney(li.statedAmount) ?? 0)),
  );

  return {
    net: serializeMoney(paid.minus(deductions)),
    deductionTotal: serializeMoney(deductions),
    contributingLines,
  };
}

/**
 * Ids of the advices that contributed a usable line for this invoice — the
 * remittances whose intent this card is reporting.
 *
 * Uses the same predicate as `netStatedForInvoice`; see `contributesForInvoice`
 * for why the two must not drift apart.
 */
export function contributingAdviceIds(
  lines: StatedLine[],
  invoiceId: string | null,
): Set<string> {
  return new Set(
    lines
      .filter((li) => contributesForInvoice(li, invoiceId))
      .map((li) => li.remittanceAdviceId),
  );
}

/**
 * How much of the CONTRIBUTING remittances has already posted against this
 * invoice.
 *
 * Scoped by the remittances' cash receipts rather than "every application on
 * the invoice": an invoice can carry unrelated prior payments, and subtracting
 * those would cancel out a brand-new remittance entirely — hiding the promise
 * and the remittance framing along with it. An advice linked to no receipt
 * cannot have posted, so it contributes nothing here.
 */
function appliedForRemittance(
  applications: AppliedLine[],
  advices: AdviceLink[],
  adviceIds: Set<string>,
  invoiceId: string | null,
): string {
  const receiptIds = new Set(
    advices.flatMap((a) =>
      adviceIds.has(a.id) && a.bankTransactionId != null
        ? [a.bankTransactionId]
        : [],
    ),
  );
  return serializeMoney(
    sumMoney(
      applications
        .filter(
          (pa) =>
            pa.invoiceId === invoiceId &&
            pa.bankTransactionId != null &&
            receiptIds.has(pa.bankTransactionId),
        )
        .map((pa) => tryParseMoney(pa.appliedAmount) ?? 0),
    ),
  );
}

/**
 * Which evidence a figure came from — drives whether the card announces itself
 * as remittance-based.
 */
export type FigureBasis =
  /** A remittance advice states money for this invoice that hasn't posted. */
  | 'remittance_pending'
  /** Everything shown reflects cash already applied in the ERP. */
  | 'posted'
  /** No usable balance signal — the card must say so in words, not show $0.00. */
  | 'unknown';

export interface ThreadMoneyInput {
  /** The thread's metadata blob, as written by the detection services. */
  metadata: Record<string, unknown>;
  /** Live money fields off the linked invoice, when one is linked. */
  invoice: InvoiceMoney | null;
  invoiceId: string | null;
  remittanceAdvices: AdviceLink[];
  remittanceLineItems: StatedLine[];
  paymentApplications: AppliedLine[];
}

export interface ShortPayFigures {
  /** Invoice net amount. */
  invoiceTotal: string | null;
  /** Cash already applied to this invoice. */
  postedPaid: string | null;
  /** Net amount the remittance states for this invoice (payments − deductions). */
  statedForInvoice: string | null;
  /** Deductions folded into `statedForInvoice`. */
  deductionTotal: string | null;
  /** The part of `statedForInvoice` that has NOT posted yet. Null when none. */
  pendingRemittance: string | null;
  /** What the invoice still owes today, before any pending remittance lands. */
  outstanding: string | null;
  /**
   * Short-pay delta that will REMAIN once the pending remittance posts —
   * equals the backend's `balanceDue − netApplied`. Null when genuinely
   * unknown; can be zero or negative when the remittance fully covers the
   * balance (the card renders those in words, never as a bare `$0.00`).
   */
  shortfall: string | null;
  basis: FigureBasis;
}

function readString(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number') return String(value);
  return null;
}

/** First value that parses as money, as a canonical decimal string. */
function firstMoney(
  ...candidates: (string | null | undefined)[]
): string | null {
  for (const candidate of candidates) {
    const parsed = tryParseMoney(candidate ?? null);
    if (parsed !== null) return serializeMoney(parsed);
  }
  return null;
}

/**
 * True when this thread was opened by `detectShortPaysFromRemittanceAdvice` /
 * `detectOverPaymentsFromRemittanceAdvice`. On those threads the metadata
 * discrepancy amount is a residual (already net of the remittance), so it must
 * never be reused as "the invoice's outstanding balance".
 */
function isRemittanceDerivedMetadata(
  metadata: Record<string, unknown>,
): boolean {
  return (
    readString(metadata.remittanceAdviceId) !== null ||
    readString(metadata.netApplied) !== null
  );
}

/**
 * Shared first half of both derivations: invoice totals plus the pending
 * remittance intent for this invoice.
 */
function deriveCommon(input: ThreadMoneyInput) {
  const { metadata, invoice, invoiceId } = input;

  // Live invoice first everywhere: it is the freshest view of the money, while
  // metadata is a snapshot taken when the thread opened.
  const invoiceTotal = firstMoney(
    invoice?.netAmount,
    readString(metadata.originalAmount),
  );
  const postedPaid = firstMoney(
    invoice?.totalPaid,
    readString(metadata.totalPaid),
  );

  // Deliberately every non-rejected advice line for this invoice, NOT just the
  // one in `metadata.remittanceAdviceId`. A customer can remit against the same
  // invoice more than once, and the backend attaches all of those lines to the
  // single open thread: `findOrOpenDetectedCollectionThread` dedupes on
  // (org, customer, invoice) and `linkLineItemsToThread` links each new advice's
  // lines to the thread it reused, without rewriting the metadata id. The
  // evidence gather mirrors that — it loads all `RemittanceLineItem` rows for
  // the invoice precisely so consumers can compute stated intent for it. Scoping
  // to the first advice id would drop later promises and overstate the short.
  const { net, deductionTotal, contributingLines } = netStatedForInvoice(
    input.remittanceLineItems,
    invoiceId,
  );
  const hasStatedLines = contributingLines.length > 0;
  const adviceIds = contributingAdviceIds(input.remittanceLineItems, invoiceId);
  const applied = appliedForRemittance(
    input.paymentApplications,
    input.remittanceAdvices,
    adviceIds,
    invoiceId,
  );
  const pendingRaw = subtractMoney(net, applied);
  const pendingRemittance =
    hasStatedLines && compareMoney(pendingRaw, EPSILON) > 0
      ? serializeMoney(pendingRaw)
      : null;

  // `shortPayAmount` is only a balance on threads that did NOT come from a
  // remittance; on remittance threads it is already net of the remittance and
  // reusing it here is what produced the "$0.00 short" bug.
  const metadataBalance = isRemittanceDerivedMetadata(metadata)
    ? null
    : readString(metadata.shortPayAmount);

  const derivedOutstanding =
    invoiceTotal !== null && postedPaid !== null
      ? serializeMoney(subtractMoney(invoiceTotal, postedPaid))
      : null;

  const outstanding = firstMoney(
    invoice?.balanceDue,
    readString(metadata.balanceDueAtDetection),
    metadataBalance,
    derivedOutstanding,
  );

  return {
    invoiceTotal,
    postedPaid,
    statedForInvoice: hasStatedLines ? net : null,
    deductionTotal: hasStatedLines ? deductionTotal : null,
    pendingRemittance,
    outstanding,
    contributingLines,
  };
}

/**
 * Money figures for a `short_pay` thread's summary card.
 *
 * `shortfall` is deliberately NOT clamped at zero: a remittance that covers the
 * whole balance produces a zero/negative delta, and the card says that in words
 * rather than printing a misleading `$0.00` next to a "Short" label.
 */
export function deriveShortPayFigures(
  input: ThreadMoneyInput,
): ShortPayFigures {
  const common = deriveCommon(input);
  const { outstanding, pendingRemittance } = common;

  const shortfall =
    outstanding === null
      ? null
      : serializeMoney(subtractMoney(outstanding, pendingRemittance ?? '0'));

  const basis: FigureBasis =
    pendingRemittance !== null
      ? 'remittance_pending'
      : outstanding !== null
        ? 'posted'
        : 'unknown';

  return {
    invoiceTotal: common.invoiceTotal,
    postedPaid: common.postedPaid,
    statedForInvoice: common.statedForInvoice,
    deductionTotal: common.deductionTotal,
    pendingRemittance,
    outstanding,
    shortfall,
    basis,
  };
}

export interface OverPaymentFigures {
  invoiceTotal: string | null;
  postedPaid: string | null;
  statedForInvoice: string | null;
  pendingRemittance: string | null;
  outstanding: string | null;
  /** Credit owed back once everything lands. Null when genuinely unknown. */
  overPayment: string | null;
  /**
   * True when `overPayment` is the live projection off the pending remittance
   * (`pendingRemittance − outstanding`) rather than a booked credit or a
   * metadata snapshot.
   *
   * Distinct from `basis === 'remittance_pending'`, which only says a remittance
   * has unposted intent for this invoice. Both can be true, but a thread can
   * also carry a pending remittance that does NOT project a credit — a stale or
   * contradictory `over_payment` thread whose remittance no longer exceeds the
   * balance. There the credit falls back to metadata, and the card must not
   * label it "projected" or claim it "comes from a remittance advice".
   *
   * Also the card's guarantee that `outstanding` is non-null: this can only be
   * true when the projection was computable. Keep the callout gated on it, or
   * the balance in that sentence renders as an em dash.
   */
  creditIsProjected: boolean;
  basis: FigureBasis;
}

/**
 * Money figures for an `over_payment` thread's summary card — the mirror of
 * `deriveShortPayFigures`.
 *
 * Same defect family: `detectOverPaymentsFromRemittanceAdvice` writes
 * `projectedOverPaymentAmount` with no `totalPaid`, so the card fell back to
 * `invoice.total_paid` and rendered "Received $0.00" beside a real credit. The
 * pending remittance is rolled into "Received + promised" here too.
 */
export function deriveOverPaymentFigures(
  input: ThreadMoneyInput,
): OverPaymentFigures {
  const common = deriveCommon(input);
  const { outstanding, pendingRemittance } = common;

  // A pending remittance that exceeds the balance is the live projection; it
  // beats any snapshot amount in metadata.
  const projectedFromRemittance =
    outstanding !== null && pendingRemittance !== null
      ? subtractMoney(pendingRemittance, outstanding)
      : null;

  // A negative balance means the ERP has already booked the over-payment.
  const parsedBalance = tryParseMoney(input.invoice?.balanceDue ?? null);
  const bookedCredit =
    parsedBalance !== null && parsedBalance.isNegative()
      ? serializeMoney(parsedBalance.abs())
      : null;

  // Only a projection that actually clears the balance is the credit. A pending
  // remittance that under-covers projects nothing, so the headline falls back
  // to booked/metadata and the card drops the "projected" framing with it.
  const creditIsProjected =
    projectedFromRemittance !== null &&
    compareMoney(projectedFromRemittance, EPSILON) > 0;

  const overPayment = creditIsProjected
    ? serializeMoney(projectedFromRemittance)
    : firstMoney(
        bookedCredit,
        readString(input.metadata.overPaymentAmount),
        readString(input.metadata.projectedOverPaymentAmount),
      );

  // `basis` still tracks unposted remittance intent, not where the credit came
  // from: the pending amount is folded into "Received + promised" either way,
  // and that stat has to stay labelled as promised money.
  const basis: FigureBasis =
    pendingRemittance !== null
      ? 'remittance_pending'
      : overPayment !== null || outstanding !== null
        ? 'posted'
        : 'unknown';

  return {
    invoiceTotal: common.invoiceTotal,
    postedPaid: common.postedPaid,
    statedForInvoice: common.statedForInvoice,
    pendingRemittance,
    outstanding,
    overPayment,
    creditIsProjected,
    basis,
  };
}

/**
 * "Received + promised": posted cash plus anything a remittance has promised
 * but not yet applied. Returns null when neither is known — the caller renders
 * that as words, not `$0.00`.
 */
export function receivedPlusPromised(
  postedPaid: string | null,
  pendingRemittance: string | null,
): string | null {
  if (postedPaid === null && pendingRemittance === null) return null;
  return serializeMoney(
    sumMoney([postedPaid ?? '0', pendingRemittance ?? '0']),
  );
}
